import { useEffect, useState } from 'react';
import { getShopGameWalletSyncIssues } from '../../../services/api';
import type { ShopGameWalletSyncIssue } from '../../../types';

interface Props {
  providerId: string;
}

export default function SyncIssuesTab({ providerId }: Props) {
  const [issues, setIssues] = useState<ShopGameWalletSyncIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getShopGameWalletSyncIssues({ providerId })
      .then((res) => {
        if (res.success && res.data) {
          setIssues(res.data.items);
        } else {
          setError(res.message || 'Failed to load sync issues');
        }
      })
      .catch(() => setError('Failed to load sync issues'))
      .finally(() => setLoading(false));
  }, [providerId]);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700">
        Retry jobs and provider health controls stay Admin-only.
      </div>

      {loading && <p className="py-8 text-center text-sm text-stone-400">Loading…</p>}
      {error && <p className="py-6 text-center text-sm text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50">
                {['Issue ID', 'Player', 'Game wallet ID', 'Issue type', 'Error message', 'Status'].map((h) => (
                  <th key={h} className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {issues.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-stone-400">
                    No sync issues found
                  </td>
                </tr>
              )}
              {issues.map((issue) => (
                <tr key={issue.id} className="hover:bg-stone-50">
                  <td className="px-3 py-3 font-mono text-xs text-stone-700">{issue.id}</td>
                  <td className="px-3 py-3 text-sm text-stone-900">{issue.playerName}</td>
                  <td className="px-3 py-3 font-mono text-xs text-stone-700">{issue.gameWalletId}</td>
                  <td className="px-3 py-3 text-sm text-stone-700">{issue.issueType}</td>
                  <td className="px-3 py-3 text-sm text-stone-500">{issue.errorMessage}</td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                        issue.status === 'failed'
                          ? 'border-red-200 bg-red-50 text-red-700'
                          : 'border-amber-200 bg-amber-50 text-amber-700'
                      }`}
                    >
                      {issue.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
